import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Page, Spinner, ErrorNote, TaskStatusBadge, TaskPriorityBadge, fmtDay } from '../components.jsx';
import HomeBoard from '../components/HomeBoard.jsx';

function AnnouncementsPanel() {
  const [items, setItems] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const load = () => api.get('/api/manager/announcements').then(setItems).catch((e) => setError(e.message));
  // load() implicitly returns the fetch promise -- passed directly to useEffect,
  // React would treat that promise as the effect's cleanup ("destroy") callback.
  // Dev builds warn about this; the production build skips that check and just
  // calls the returned value as a function on unmount, throwing "n is not a
  // function". Wrap it so the effect callback itself returns undefined.
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    try {
      await api.post('/api/manager/announcements', { message: message.trim() });
      setMessage('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleActive = async (item) => {
    await api.patch(`/api/manager/announcements/${item.id}`, { active: !item.active });
    load();
  };

  const remove = async (item) => {
    if (!window.confirm('Delete this notice?')) return;
    await api.del(`/api/manager/announcements/${item.id}`);
    load();
  };

  return (
    <div className="card p-4">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-steel">News ticker</h2>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form onSubmit={create} className="mb-4 flex gap-2">
        <input className="input" placeholder="New company-wide notice…" value={message} onChange={(e) => setMessage(e.target.value)} />
        <button className="btn-primary shrink-0" type="submit">Post</button>
      </form>
      {!items && <Spinner label="Loading notices…" />}
      {items && items.length === 0 && <p className="text-sm text-steel">No notices yet.</p>}
      {items && items.length > 0 && (
        <ul className="divide-y divide-steel/10">
          {items.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className={item.active ? '' : 'text-steel line-through'}>{item.message}</span>
              <div className="flex shrink-0 gap-2">
                <button className="text-xs text-steel underline" onClick={() => toggleActive(item)}>
                  {item.active ? 'Deactivate' : 'Reactivate'}
                </button>
                <button className="text-xs text-ascred underline" onClick={() => remove(item)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UserLayoutsPanel() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/api/manager/users').then(setUsers).catch((e) => setError(e.message));
  }, []);

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-steel">Edit a user's layout</h2>
        {error && <ErrorNote>Couldn't load users from PocketID ({error})</ErrorNote>}
        {!users && !error && <Spinner label="Loading users…" />}
        {users && (
          <select className="input" value={selected || ''} onChange={(e) => setSelected(e.target.value || null)}>
            <option value="">Select a user…</option>
            {users.map((u) => (
              <option key={u.sub} value={u.sub}>{u.name} {u.email ? `(${u.email})` : ''}</option>
            ))}
          </select>
        )}
      </div>
      {selected && (
        <div>
          <p className="mb-3 text-xs text-steel">
            Editing here changes exactly what this person sees — add apps they need but aren't assigned in PocketID, or hide ones that are forbidden.
          </p>
          <HomeBoard
            key={selected}
            apiBase={`/api/manager/users/${selected}`}
            loadUrl={`/api/manager/users/${selected}/home`}
            editMode={true}
          />
        </div>
      )}
    </div>
  );
}

function SalesTodoPanel() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/manager/sales-todo').then(setGroups).catch((e) => setError(e.message));
  }, []);

  return (
    <div className="card p-4">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-steel">Sales to-do — team view</h2>
      {error && <ErrorNote>{error}</ErrorNote>}
      {!groups && !error && <Spinner label="Loading team to-dos…" />}
      {groups && groups.length === 0 && <p className="text-sm text-steel">Nothing outstanding right now.</p>}
      {groups && groups.map((g) => (
        <div key={g.assignedUserName} className="mb-4 last:mb-0">
          <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-navy">
            {g.assignedUserName} <span className="text-steel">({g.items.length})</span>
          </h3>
          <ul className="divide-y divide-steel/10">
            {g.items.map((item) => (
              <li key={item.id} className="py-2 text-sm">
                <a href={item.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">
                  {item.name}
                </a>
                <div className="text-xs text-steel">
                  {item.opportunityName ? (
                    <a href={item.opportunityUrl} target="_blank" rel="noreferrer" className="hover:underline">
                      {item.opportunityName}
                    </a>
                  ) : (
                    '—'
                  )}
                  {' · '}
                  <TaskStatusBadge status={item.status} /> <TaskPriorityBadge priority={item.priority} />
                </div>
                <div className="text-xs text-steel">
                  {item.taskType || '—'} · due {fmtDay(item.dueDate)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { id: 'users', label: 'User layouts' },
  { id: 'salesTodo', label: 'Sales to-do' },
  { id: 'announcements', label: 'News ticker' },
];

export default function ManagePage() {
  const [tab, setTab] = useState('users');

  return (
    <Page wide>
      <h1 className="mb-4 text-xl font-bold">Manage</h1>
      <div className="mb-4 flex gap-1 rounded-lg bg-steel/10 p-1 text-sm">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 rounded-md px-3 py-2 font-medium transition ${tab === t.id ? 'bg-navy text-white' : 'text-steel hover:bg-steel/10'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'users' ? <UserLayoutsPanel /> : tab === 'salesTodo' ? <SalesTodoPanel /> : <AnnouncementsPanel />}
    </Page>
  );
}
